
import { appRoute } from "@/components/routes/app-route";
import { useSetAppSearch } from '@/components/routes/app-route.hooks';
import { AppSearch } from "@/components/routes/app-route.zod";
import { minDelay } from "@/lib/timing";
import { otpGraphql } from "@/services/otp";
import useSWR from "swr";
import type { PlanConnectionVariables } from "../types/otp/plan-connection.graphql";
import { getPlanKey } from './use-is-selected-plan';

const PLANNER_HOOK_KEY = "PLANNER_HOOK_REQUEST_INTERNAL";

type INTERNAL__Input = ReturnType<typeof queryGraphql>
function INTERNAL__useUpdatePlannerState() {
  const { set } = useSetAppSearch()

  return async (promise: INTERNAL__Input) => {
    const p = await promise;
    const potentialFirstItinerary = p.extended.itineraries?.valid[0]
    if (!potentialFirstItinerary) {
      return;
    }

    const key = getPlanKey(potentialFirstItinerary)
    set((prev) => {
      if (!prev.data) {
        return prev
      }

      prev.data.selected = key;

      return prev
    })
  }
}

export function usePlanner(artificialDelay = 250) {
  const search = appRoute.useSearch({
    select: (s) => ({ from: s.from, to: s.to, data: s.data }),
  })
  const update = INTERNAL__useUpdatePlannerState()

  const { from, to, data } = search;
  const potentialData = typeof data?.opts === 'object' ? data.opts : undefined

  const res = useSWR(
    [
      PLANNER_HOOK_KEY,
      artificialDelay,
      from?.key,
      to?.key,
      potentialData?.mode,
      potentialData?.time,
      data?.transportType,
    ],
    () => {
      const plannerPromise = queryGraphql(search)
      update(plannerPromise)

      if (artificialDelay <= 0) {
        return plannerPromise
      } else {
        return minDelay(plannerPromise, artificialDelay)
      }
    },
    {
      revalidateOnFocus: false,
      revalidateIfStale: false,
    },
  )

  return {
    hasValidQuery: !!from && !!to,
    ...res
  }
}

/**
 * Maps AppSearch to PlanConnectionVariables for the planConnection GraphQL query.
 * - Only supports direct mode "WALK" (as no modes are present in schema).
 * - Handles "now" and plannerOptions for time/arriveBy.
 */
function graphqlConnectionProps({ from, to, data }: AppSearch): PlanConnectionVariables | undefined {
  if (!from || !to) {
    return;
  }

  // Map from AppPlace to GraphQL input
  const origin = {
    location: {
      coordinate: {
        latitude: from.latlng[0],
        longitude: from.latlng[1],
      },
    },
  };

  const destination = {
    location: {
      coordinate: {
        latitude: to.latlng[0],
        longitude: to.latlng[1],
      },
    },
  };

  // Always use "en" locale
  const locale = "en";

  // Only "WALK" is supported as direct mode (no modes in schema)
  // Ensure modes.direct is a mutable array (not readonly)
  const modes = {
    direct: ["FLEX", "WALK"],
    transit: {
      transit: [
        { mode: "BUS" },
        { mode: "FERRY" },
    ]
    }
  } satisfies PlanConnectionVariables["modes"];

  // Default search window: 1 hour
  const searchWindow = "1h";
  const searchWindowMiddleware = 3600 * 2;

  // Handle time and arriveBy/leaveAfter
  let dateTime: PlanConnectionVariables["dateTime"] | undefined = undefined;
  // data?.opts can be undefined, "now", or { mode, time }
  if (data?.opts && data.opts !== "now") {
    // data.opts is { mode, time }
    const t = new Date(data.opts.time);
    const iso = t.toISOString();
    if (data.opts.mode === "a") {
      dateTime = { latestArrival: iso };
    } else {
      dateTime = { earliestDeparture: iso };
    }
  }

  // Compose the variables object
  const variables: PlanConnectionVariables = {
    origin,
    destination,
    transportType: data?.transportType,
    modes,
    locale,
    searchWindow,
    searchWindowMiddleware,
    ...(dateTime ? { dateTime } : {}),
  };

  return variables;
}

/**
 * Local shadow testing for simple server response validation
 */
async function queryGraphql(search: AppSearch) {
  const newRes = await otpGraphql.planConnection(graphqlConnectionProps(search))
  return newRes.data
}
